import CloseIcon from "icons/CloseIcon";
import CopyIcon from "icons/CopyIcon";
import React from "react";
import { useHistory, useLocation } from "react-router-dom";
import Checkbox from "components/checkbox/Checkbox";
import ThemeSettingsModal from "components/themeSettingsModal/ThemeSettingsModal";
import { ThemeSettingsValue } from "components/themeSettingsModal/themeSettingsUtils";
import Select from "components/select/Select";
import UploadAttachment from "components/UploadAttachment";
import ToggleButton from "components/toggleButton/ToggleButton";
import first from "lodash/first";
import sortBy from "lodash/sortBy";
import times from "lodash/times";
import {
  addDisplayPreset,
  addRemoteScreen,
  deleteDisplayPreset,
} from "util/pouchActions";
import { getDivisionDoc } from "util/lifterHelper";
import { updateAttributesOnDocument } from "util/pouchActions";
import { useTheme } from "app/ThemeProvider";
import { useMeet } from "util/useMeet";
import { useStatus } from "util/useStatus";
import { WidgetId, useDisplaySettings } from "../DisplaySettingsProvider";
import { toastr } from "react-redux-toastr";
import { usePlatform } from "util/usePlatform";

import "./DisplaySettingsPanel.scss";
import find from "lodash/find";

const components: Record<Exclude<WidgetId, "theme">, string> = {
  b: "Lifter Name",
  a: "Attempt Info",
  t: "Team Name",
  d: "Weight Class & Divisions",
  r: "Record Attempts",
  p: "Position Change",
  time: "Timer",
  l: "Ref Lights",
  c: "Loading Chart",
  o: "Lifting Order",
  sb: "Scoreboard",
  n: "Next bar load",
  no: "Next Lifters",
  co: "Live Stream Bar",
  m: "Marquee",
  s: "Standings",
  tx: "Text",
  i: "Image",
};

const normalizeSearch = (search: string) => {
  if (!search) {
    return "";
  }

  if (search.startsWith("?")) {
    return search;
  }

  return `?${search}`;
};

export const DisplaySettingsPanel = ({
  onCloseSettings,
  remoteMode,
  setRemoteMode,
  selectedRemoteScreenId,
  setSelectedRemoteScreenId,
  remoteScreenUrl,
}: {
  onCloseSettings: () => void;
  remoteMode: boolean;
  setRemoteMode: React.Dispatch<React.SetStateAction<boolean>>;
  selectedRemoteScreenId: string | null;
  setSelectedRemoteScreenId: React.Dispatch<
    React.SetStateAction<string | null>
  >;
  remoteScreenUrl: string;
}) => {
  const {
    state,
    updateComponent,
    setIsTableSettingsModalOpen,
    updateRemoteScreenConfig,
    resetToDefaults,
    reloadStateFromUrl,
  } = useDisplaySettings();

  const meet = useMeet();
  const platform = usePlatform();
  const { theme: appTheme, customTheme: appCustomTheme } = useTheme();
  const { isLocal } = useStatus();
  const history = useHistory();
  const location = useLocation();
  const [isThemeModalOpen, setIsThemeModalOpen] = React.useState(false);

  const themeSettings = React.useMemo((): ThemeSettingsValue => {
    return {
      theme: state.theme.theme?.theme ?? appTheme ?? "dark",
      customTheme: state.theme.theme?.customTheme ?? appCustomTheme,
    };
  }, [appCustomTheme, appTheme, state.theme.theme]);

  const remoteScreens = React.useMemo(() => {
    return sortBy(Object.values(meet.remoteScreens ?? {}), "name");
  }, [meet.remoteScreens]);

  const displayPresets = React.useMemo(() => {
    return sortBy(Object.values(meet.displayPresets ?? {}), "name");
  }, [meet.displayPresets]);

  const onCreateDisplayPreset = React.useCallback(() => {
    const newPresetName = prompt("Enter name for new preset");
    if (!newPresetName) {
      return;
    }

    if (displayPresets.find((preset) => preset.name === newPresetName)) {
      alert("Preset name must be unique");
      return;
    }

    addDisplayPreset({
      meetId: meet._id,
      platformId: platform._id,
      search: window.location.search,
      name: newPresetName,
    });

    toastr.success("Preset created.", "");
  }, [displayPresets, meet._id, platform._id]);

  const loadDisplayPreset = React.useCallback(
    (presetId: string) => {
      const preset = meet.displayPresets?.[presetId];
      if (!preset) {
        return;
      }

      const nextPathname = preset.platformId
        ? location.pathname.replace(platform._id, preset.platformId)
        : location.pathname;
      const nextSearch = normalizeSearch(preset.search);

      history.replace({
        pathname: nextPathname,
        search: nextSearch,
      });

      if (remoteMode && selectedRemoteScreenId) {
        updateAttributesOnDocument(meet._id, selectedRemoteScreenId, {
          search: nextSearch,
          platformId: preset.platformId ?? platform._id,
        });
      }

      reloadStateFromUrl();
    },
    [
      history,
      location.pathname,
      meet._id,
      meet.displayPresets,
      platform._id,
      reloadStateFromUrl,
      remoteMode,
      selectedRemoteScreenId,
    ],
  );

  const saveDisplayPreset = React.useCallback(
    (presetId: string) => {
      updateAttributesOnDocument(meet._id, presetId, {
        search: window.location.search,
        platformId: platform._id,
      });

      toastr.success("Preset saved.", "");
    },
    [meet._id, platform._id],
  );

  const removeDisplayPreset = React.useCallback(
    (presetId: string) => {
      const preset = meet.displayPresets?.[presetId];
      if (!preset) {
        return;
      }

      const shouldDelete = window.confirm(`Delete preset "${preset.name}"?`);

      if (!shouldDelete) {
        return;
      }

      deleteDisplayPreset(preset, meet._id);
      toastr.success("Preset deleted.", "");
    },
    [meet._id, meet.displayPresets],
  );

  const onCreateRemoteScreen = () => {
    const newScreenName = prompt("Enter name for new screen");
    if (!newScreenName) {
      return;
    }

    if (remoteScreens.find((s) => s.name === newScreenName)) {
      alert("Screen name must be unique");
      return;
    }

    const nextId = addRemoteScreen({
      meetId: meet._id,
      platformId: platform._id,
      search: window.location.search,
      name: newScreenName,
    });
    setRemoteMode(true);
    setSelectedRemoteScreenId(nextId);
  };

  const setAsDefault = () => {
    updateAttributesOnDocument(meet._id, meet._id, {
      displayDefaultURL: window.location.search,
    });
    toastr.success("Display configuration set as meet default.", "");
  };

  const platformOptions = React.useMemo(() => {
    return sortBy(Object.values(meet.platforms ?? {}), "name").map(
      (platform) => {
        return {
          value: platform._id,
          label: platform.name || platform._id,
        };
      },
    );
  }, [meet.platforms]);

  const remoteScreenOptions = React.useMemo(() => {
    return sortBy(remoteScreens, "name").map((screen) => {
      return {
        value: screen._id,
        label: screen.name,
      };
    });
  }, [remoteScreens]);

  const divisionOptions = React.useMemo(() => {
    const sortedDivisions = sortBy(meet.divisions, "name");

    return sortedDivisions
      .map((division) => {
        return { value: division._id, label: division.name };
      })
      .filter((division) => {
        return !!find(meet.lifters, (lifter) => {
          return !!find(
            lifter.divisions,
            (ld) => ld.divisionId === division.value,
          );
        });
      });
  }, [meet.divisions, meet.lifters]);

  const weightClassOptions = React.useMemo(() => {
    const standingsDivisionId = state.s.sd;
    if (standingsDivisionId) {
      const division = getDivisionDoc(standingsDivisionId, meet);
      if (division) {
        return Object.values(division.weightClasses).map((weightClass) => {
          return { value: weightClass._id, label: weightClass.name };
        });
      }
    }

    return [];
  }, [meet, state.s.sd]);

  const imageOptions = React.useMemo(() => {
    const attachments = meet.attachments;

    if (!attachments) {
      return [];
    }

    return Object.values(attachments).map((a) => {
      const attachment = first(Object.keys(a?._attachments ?? {}));

      return { value: attachment, label: attachment };
    });
  }, [meet.attachments]);

  const changeRemoteScreen = React.useCallback(
    (newRemoteScreenId: string | null) => {
      if (newRemoteScreenId) {
        const remoteScreen = meet.remoteScreens?.[newRemoteScreenId];
        if (remoteScreen) {
          const newPathname = location.pathname.replace(
            platform._id,
            remoteScreen.platformId,
          );

          history.replace({
            pathname: newPathname,
            search: remoteScreen.search,
          });

          reloadStateFromUrl();
        }
      }

      setSelectedRemoteScreenId(newRemoteScreenId);
    },
    [
      history,
      location.pathname,
      meet.remoteScreens,
      platform._id,
      reloadStateFromUrl,
      setSelectedRemoteScreenId,
    ],
  );

  const copyRemoteScreenUrl = React.useCallback(async () => {
    if (!remoteScreenUrl) {
      return;
    }

    try {
      if (window.isSecureContext && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(remoteScreenUrl);
      } else {
        const tempInput = document.createElement("textarea");
        tempInput.value = remoteScreenUrl;
        tempInput.setAttribute("readonly", "");
        tempInput.style.position = "absolute";
        tempInput.style.left = "-9999px";
        document.body.appendChild(tempInput);
        tempInput.select();
        document.execCommand("copy");
        document.body.removeChild(tempInput);
      }

      toastr.success("Remote screen URL copied.", "");
    } catch {
      toastr.error("Could not copy remote screen URL.", "");
    }
  }, [remoteScreenUrl]);

  return (
    <div className="display-settings-panel">
      <ThemeSettingsModal
        isOpen={isThemeModalOpen}
        onRequestClose={() => setIsThemeModalOpen(false)}
        value={themeSettings}
        onChange={(value) => {
          updateComponent({
            componentId: "theme",
            newValues: {
              theme: value,
            },
          });
        }}
      />
      <button
        aria-label="Close display settings"
        title="Close display settings"
        className="close-button"
        onClick={onCloseSettings}
      >
        <CloseIcon />
      </button>
      <div className="display-settings-modal-content">
        {isLocal && (
          <div className="local-actions">
            <button onClick={setAsDefault}>Set as default for this meet</button>
            <button onClick={resetToDefaults}>Reset to system default</button>
          </div>
        )}
        <label>
          <div className="field-title">Platform</div>
          <Select
            clearable={false}
            name="remotePlatform"
            value={platform._id}
            options={platformOptions}
            onChange={(value) => {
              if (typeof value === "string" && value !== platform._id) {
                const newPathname = location.pathname.replace(
                  platform._id,
                  value,
                );
                history.replace({
                  pathname: newPathname,
                  search: location.search,
                });
                updateRemoteScreenConfig(value);
              }
            }}
          />
        </label>

        <section
          className="settings-section"
          aria-labelledby="display-settings-presets-heading"
        >
          <h2 id="display-settings-presets-heading" className="section-heading">
            Presets
          </h2>
          <div className="section-action-row">
            <button onClick={onCreateDisplayPreset}>Create New Preset</button>
          </div>
          {displayPresets.length === 0 && (
            <div className="preset-empty-state">No presets yet.</div>
          )}
          {displayPresets.map((preset) => {
            return (
              <div key={preset._id} className="preset-card">
                <div className="preset-card-title">{preset.name}</div>
                <div className="preset-card-actions">
                  <button onClick={() => loadDisplayPreset(preset._id)}>
                    Load
                  </button>
                  <button onClick={() => saveDisplayPreset(preset._id)}>
                    Save
                  </button>
                  <button onClick={() => removeDisplayPreset(preset._id)}>
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
        </section>

        <section
          className="settings-section"
          aria-labelledby="display-settings-remote-heading"
        >
          <h2 id="display-settings-remote-heading" className="section-heading">
            Remote Screens
          </h2>
          <ToggleButton
            label="Remote Mode"
            value={remoteMode}
            onCheck={(value) => {
              setRemoteMode(value);
              setSelectedRemoteScreenId(null);
            }}
          />
          {remoteMode && (
            <div className="remote-settings-content">
              <div className="section-action-row">
                <button onClick={onCreateRemoteScreen}>
                  Create Remote Screen
                </button>
              </div>
              <label>
                Remote Screen
                <Select
                  clearable={true}
                  name="remoteScreen"
                  value={selectedRemoteScreenId}
                  options={remoteScreenOptions}
                  onChange={(value) =>
                    changeRemoteScreen(typeof value === "string" ? value : null)
                  }
                />
              </label>
              {remoteScreenUrl && (
                <label>
                  <div className="field-title">Remote Screen URL</div>
                  <div className="remote-screen-url-row">
                    <input type="text" value={remoteScreenUrl} readOnly />
                    <button
                      type="button"
                      className="copy-remote-screen-url-button"
                      onClick={copyRemoteScreenUrl}
                      aria-label="Copy remote screen URL"
                      title="Copy remote screen URL"
                    >
                      <CopyIcon />
                    </button>
                  </div>
                </label>
              )}
            </div>
          )}
        </section>

        <section
          className="settings-section"
          aria-labelledby="display-settings-theme-heading"
        >
          <h2 id="display-settings-theme-heading" className="section-heading">
            Theme
          </h2>
          <div className="section-action-row">
            <button
              onClick={() => {
                setIsThemeModalOpen(true);
              }}
            >
              Open theme settings
            </button>
          </div>
        </section>

        <section
          className="settings-section"
          aria-labelledby="display-settings-components-heading"
        >
          <h2
            id="display-settings-components-heading"
            className="section-heading"
          >
            Components
          </h2>
          {Object.keys(components).map((c) => {
            const componentId = c as Exclude<WidgetId, "theme">;
            const componentState = state[componentId] || {};
            return (
              <div key={c} className="component-row">
                <Checkbox
                  key={c}
                  label={components[componentId]}
                  value={!!componentState.v}
                  onCheck={(v) =>
                    updateComponent({ componentId, newValues: { v } })
                  }
                />
                {c === "r" && (
                  <div className="component-subcontrol">
                    <label>
                      Max Records To Show
                      <Select
                        clearable={false}
                        name="maxRecordsToShow"
                        value={componentState.mr}
                        options={[
                          { label: "1", value: 1 },
                          { label: "2", value: 2 },
                          { label: "3", value: 3 },
                          { label: "4", value: 4 },
                          { label: "5", value: 5 },
                        ]}
                        onChange={(value) =>
                          typeof value === "number" &&
                          updateComponent({
                            componentId,
                            newValues: { mr: value },
                          })
                        }
                      />
                    </label>
                  </div>
                )}
                {c === "d" && (
                  <div className="component-subcontrol">
                    <label>
                      Max Divisions To Show
                      <Select
                        clearable={false}
                        name="maxDivisionsToShow"
                        value={componentState.md}
                        options={[
                          { label: "1", value: 1 },
                          { label: "2", value: 2 },
                          { label: "3", value: 3 },
                          { label: "4", value: 4 },
                          { label: "5", value: 5 },
                        ]}
                        onChange={(value) =>
                          typeof value === "number" &&
                          updateComponent({
                            componentId,
                            newValues: { md: value },
                          })
                        }
                      />
                    </label>
                  </div>
                )}
                {(c === "o" || c === "sb") && (
                  <div className="component-subcontrol">
                    <button onClick={() => setIsTableSettingsModalOpen(true)}>
                      Open table settings
                    </button>
                  </div>
                )}
                {c === "m" && (
                  <div className="component-subcontrol">
                    <label>
                      Speed
                      <Select
                        clearable={false}
                        name="marqueeSpeed"
                        value={componentState.ms}
                        options={[
                          { label: "Slow", value: 50 },
                          { label: "Medium", value: 100 },
                          { label: "Fast", value: 150 },
                          { label: "Really Fast", value: 250 },
                        ]}
                        onChange={(value) =>
                          typeof value === "number" &&
                          updateComponent({
                            componentId,
                            newValues: { ms: value },
                          })
                        }
                      />
                    </label>
                  </div>
                )}
                {c === "tx" && (
                  <div className="component-subcontrol">
                    <input
                      aria-label="Free Text"
                      type="text"
                      name="text"
                      value={componentState.tx}
                      onChange={(event) =>
                        updateComponent({
                          componentId,
                          newValues: { tx: event.target.value },
                        })
                      }
                    />
                  </div>
                )}
                {c === "i" && (
                  <div className="component-subcontrol">
                    <label>
                      Image Source
                      <Select
                        clearable={true}
                        name="imageSrc"
                        value={componentState.is}
                        options={imageOptions}
                        onChange={(value) =>
                          typeof value === "string" &&
                          updateComponent({
                            componentId,
                            newValues: { is: value },
                          })
                        }
                      />
                    </label>
                    <div className="upload-attachment-row">
                      <UploadAttachment
                        meet={meet}
                        onComplete={(value) =>
                          updateComponent({
                            componentId,
                            newValues: { is: value },
                          })
                        }
                      />
                    </div>
                  </div>
                )}
                {c === "s" && (
                  <div className="component-subcontrol">
                    <label>
                      Show Top
                      <Select
                        clearable={false}
                        name="standingsCount"
                        value={componentState.sc}
                        options={times(15, (i) => {
                          return { label: String(i + 1), value: i + 1 };
                        })}
                        onChange={(value) =>
                          typeof value === "number" &&
                          updateComponent({
                            componentId,
                            newValues: { sc: value },
                          })
                        }
                      />
                    </label>
                    <label>
                      Division
                      <Select
                        clearable={true}
                        name="standingsDivision"
                        value={componentState.sd}
                        options={divisionOptions}
                        onChange={(value) => {
                          if (typeof value !== "string") {
                            return;
                          }

                          const division = getDivisionDoc(value, meet);
                          const firstWeightClass = first(
                            Object.values(division?.weightClasses ?? {}),
                          );

                          updateComponent({
                            componentId,
                            newValues: { sd: value, sw: firstWeightClass?._id },
                          });
                        }}
                      />
                    </label>
                    <label>
                      Weight Class
                      <Select
                        clearable={true}
                        name="standingsWeightClassId"
                        value={componentState.sw}
                        options={weightClassOptions}
                        onChange={(value) =>
                          typeof value === "string" &&
                          updateComponent({
                            componentId,
                            newValues: { sw: value },
                          })
                        }
                      />
                    </label>
                  </div>
                )}
              </div>
            );
          })}
        </section>
      </div>
    </div>
  );
};
